import { Box, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { blue, green, purple, red, yellow } from "@mui/material/colors";
import { DefaultizedPieValueType } from "@mui/x-charts";
import DonutChart from "components/Dashboard/DonutChart";
import { useMemo } from "react";

import { ChemicalFacilitiesSummaryQuery } from "../../../../../generated-graphql/graphql";

/**
 * Status IDs that map to omnisearch filter keys
 */
export type ReportStatusId =
  | "NOT_REPORTING"
  | "NO_REPORT"
  | "AWAITING_VERIFICATION"
  | "PROCESSING"
  | "FILING_COMPLETE";

type Props = {
  data?: ChemicalFacilitiesSummaryQuery["reportingSeasonSummary"];
  loading: boolean;
  onStatusClick?: (statusId: ReportStatusId) => void;
  /** Currently selected status ID for visual highlighting */
  selectedStatusId?: ReportStatusId | null;
};

/** Active colors for selected segments */
const STATUS_COLORS = {
  NOT_REPORTING: red[300],
  NO_REPORT: purple[200],
  AWAITING_VERIFICATION: yellow[400],
  PROCESSING: blue[300],
  FILING_COMPLETE: green[200],
};

/** Faded colors for unselected segments */
const STATUS_COLORS_INACTIVE = {
  NOT_REPORTING: red[100],
  NO_REPORT: purple[50],
  AWAITING_VERIFICATION: yellow[100],
  PROCESSING: blue[100],
  FILING_COMPLETE: green[50],
};

const STATUS_LABELS: Record<ReportStatusId, string> = {
  NOT_REPORTING: "Not Reporting",
  NO_REPORT: "No Report",
  AWAITING_VERIFICATION: "Awaiting Verification",
  PROCESSING: "Processing",
  FILING_COMPLETE: "Filing Complete",
};

/** Order of statuses in the chart (used for selectedIndex calculation) */
const STATUS_ORDER: ReportStatusId[] = [
  "NOT_REPORTING",
  "NO_REPORT",
  "AWAITING_VERIFICATION",
  "PROCESSING",
  "FILING_COMPLETE",
];

export function ChemicalFacilitiesDoughnutChart({
  data,
  loading,
  onStatusClick,
  selectedStatusId,
}: Props) {
  const theme = useTheme();

  const chartData = data;

  // Determine if we have an active selection
  const hasSelection = selectedStatusId != null;

  // Calculate the selected index for DonutChart highlighting
  const selectedIndex = useMemo(() => {
    if (!selectedStatusId) return null;
    return STATUS_ORDER.indexOf(selectedStatusId);
  }, [selectedStatusId]);

  // Calculate total facilities (or selected count if filtered)
  const totalFacilities = useMemo(() => {
    if (!chartData) return 0;

    if (hasSelection && selectedStatusId) {
      // Show count for selected status only
      switch (selectedStatusId) {
        case "NOT_REPORTING":
          return chartData.reportsInNotReporting ?? 0;
        case "NO_REPORT":
          return chartData.noReportsTotal ?? 0;
        case "AWAITING_VERIFICATION":
          return chartData.reportsInAwaitingVerification ?? 0;
        case "PROCESSING":
          return chartData.reportsInProcessing ?? 0;
        case "FILING_COMPLETE":
          return chartData.reportsInFilingComplete ?? 0;
        default:
          return 0;
      }
    }

    // Show total when no selection
    return (
      (chartData.reportsInNotReporting || 0) +
      (chartData.noReportsTotal || 0) +
      (chartData.reportsInAwaitingVerification || 0) +
      (chartData.reportsInProcessing || 0) +
      (chartData.reportsInFilingComplete || 0)
    );
  }, [chartData, hasSelection, selectedStatusId]);

  // Transform data for the DonutChart component
  const seriesData = useMemo(() => {
    if (!chartData) return [];

    return STATUS_ORDER.map((statusId) => {
      const isSelected = !hasSelection || selectedStatusId === statusId;
      const value = (() => {
        switch (statusId) {
          case "NOT_REPORTING":
            return chartData.reportsInNotReporting ?? 0;
          case "NO_REPORT":
            return chartData.noReportsTotal ?? 0;
          case "AWAITING_VERIFICATION":
            return chartData.reportsInAwaitingVerification ?? 0;
          case "PROCESSING":
            return chartData.reportsInProcessing ?? 0;
          case "FILING_COMPLETE":
            return chartData.reportsInFilingComplete ?? 0;
        }
      })();

      return {
        id: statusId,
        value,
        color: isSelected ? STATUS_COLORS[statusId] : STATUS_COLORS_INACTIVE[statusId],
        label: STATUS_LABELS[statusId],
      };
    });
  }, [chartData, hasSelection, selectedStatusId]);

  // Custom legend items to show facility counts with selection styling
  const legendItems = useMemo(() => {
    return seriesData.map((item) => {
      const isSelected = !hasSelection || selectedStatusId === (item.id as ReportStatusId);
      return {
        color: STATUS_COLORS[item.id as ReportStatusId],
        isSelected,
        value: (
          <Box
            display="flex"
            width="100%"
            justifyContent="space-between"
            gap={2}
            sx={{ opacity: isSelected ? 1 : 0.4 }}
          >
            <Typography variant="body2">{item.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {item.value.toLocaleString()} facilities
            </Typography>
          </Box>
        ),
      };
    });
  }, [seriesData, hasSelection, selectedStatusId]);

  const handleStatusClick = (item: DefaultizedPieValueType) => {
    if (onStatusClick) {
      onStatusClick(item.id as ReportStatusId);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 3,
        height: "100%",
      }}
    >
      {loading ? (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: theme.palette.text.secondary,
            }}
          >
            Facilities by Report Status
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "calc(100% - 60px)",
              gap: 4,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 250,
                height: 250,
                flexShrink: 0,
              }}
            >
              <Skeleton
                variant="circular"
                width={250}
                height={250}
                sx={{
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.4 },
                    "100%": { opacity: 1 },
                  },
                }}
              />
            </Box>

            <Stack spacing={1} sx={{ width: "100%" }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pb: 1,
                    animation: `slideIn 0.3s ease-out ${index * 0.1}s both`,
                    "@keyframes slideIn": {
                      from: {
                        opacity: 0,
                        transform: "translateX(-10px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={16}
                    height={16}
                    sx={{ borderRadius: "2px", flexShrink: 0 }}
                  />
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="30%" height={20} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.5s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: theme.palette.text.secondary,
            }}
          >
            Facilities by Report Status
          </Typography>
          <DonutChart
            seriesData={seriesData}
            selectedIndex={selectedIndex}
            label={totalFacilities.toLocaleString()}
            subLabel={hasSelection ? "Facilities" : "Total\nFacilities"}
            containerHeight="18rem"
            legend={{
              direction: "column",
              position: "right",
            }}
            legendItems={legendItems}
            onItemClick={(event, pieItemIdentifier, item) => handleStatusClick(item)}
            onLegendItemClick={(item) => handleStatusClick(item)}
          />
        </Box>
      )}
    </Paper>
  );
}
